// Base constants
export const CARD_COST = 3;
export const MILESTONE_COST = 8;
export const MAX_MILESTONES = 3;
export const AWARD_COSTS = [8, 14, 20];
export const MAX_AWARDS = 3;
export const DEFAULT_STEEL_VALUE = 2;
export const DEFAULT_TITANIUM_VALUE = 3;
export const FLOATERS_VALUE = 3;
export const MICROBES_VALUE = 2;
export const OCEAN_BONUS = 2;
export const CORPORATION_CARDS_DEALT_PER_PLAYER = 2;
// Global parameters
export const HEAT_FOR_TEMPERATURE = 8;
export const MAX_OCEAN_TILES = 9;
export const MAX_TEMPERATURE = 8;
export const MAX_OXYGEN_LEVEL = 14;
export const MIN_TEMPERATURE = -30;
export const MIN_OXYGEN_LEVEL = 0;
export const MIN_VENUS_SCALE = 0;
export const MAX_VENUS_SCALE = 30;
// Terrakeeping Earth global tracks: warming lives on the emissions
// tracker; the Mars oxygen/temperature/venus tracks are all dormant.
/**
 * The electricity tracks: 1 chit = 1 PWh/yr. The 2026 world demands
 * ~31 PWh (Ember: 30,856 TWh in 2024); supply starts 2 chits ahead
 * (the reserve margin the grid already carries beyond the 31 starting
 * plant tiles). Demand then grows 2 PWh per generation — one 5-year
 * generation of the ~2.5-2.7%/yr growth in Research/07 (output and
 * income growth in emerging economies); the research's four marginal
 * drivers (clean-tech manufacturing, EVs, cooling, data centres & AI)
 * arrive as demand-adding cards on top. When demand outruns supply,
 * every player loses 1 Social Acceptance per generation of shortage.
 */
/**
 * NOTE these are MIGRATION FALLBACKS, not the live setup values. A new game
 * opens both tracks at the fleet actually seated on the board (Game.newInstance,
 * after placeStartingPlants) so the supply row can never disagree with the
 * variable and firm rows drawn beside it. Keep them equal to that fleet.
 */
export const STARTING_ELECTRICITY_DEMAND_PWH = 31;
export const STARTING_ELECTRICITY_SUPPLY_PWH = 31;
/**
 * THERE IS NO COASTAL SURCHARGE (Dan, 2026-08-25). A farm on a coastal hex
 * used to pay a flat 3 Cash for mooring and subsea install. It was
 * dropped because of what the shared-lattice map did to its scope: the
 * charge was written when nine dedicated offshore zones existed, and by the
 * time it fell on 141 of ~200 hexes it had stopped being a premium for
 * working at sea and become a tax on having a coastline. A home base on the
 * water - Cairo, Mumbai, Lagos, Tokyo - paid it on almost every hex of its
 * opening ring, while an inland base paid nothing for the same ring, and
 * that is a difference nobody chose when they picked a city.
 *
 * `coastal` still does its real job: it is what lets marine plants and
 * floating solar be sited at all, and floating solar is still never a top
 * site. It just no longer prices anything.
 */
/**
 * WHAT IT COSTS TO OPEN NEW TERRITORY (Dan, 2026-08-19, renamed 2026-08-20):
 * a flat fee in CREDITS on any farm or extraction site not adjacent to a
 * tile you already own or to your home base. Building outward from your own
 * ground is free.
 *
 * ON TOP OF the copper connection, not instead of it, and the two price
 * different things in different currencies. Connection is the distance to
 * the nearest generation - the world's grid, the same number for everybody,
 * and paid in the copper the wire is made of. This is YOUR problem: a site
 * far from anything you run needs its own crews, permissions and local
 * arrangements, so sprawl costs money and consolidation does not.
 *
 * Waived outright by a completed Transmission Superhighway, and cut to
 * REDUCED_TERRITORY_FEE by the Rural Electrification Legacy endowment.
 * Shared with the client for the standard-project text.
 */
export const TERRITORY_FEE = 3;
/** What Rural Electrification Legacy pays instead: the wires are half there already. */
export const REDUCED_TERRITORY_FEE = 1;
/**
 * WHAT IT COSTS TO BUILD NEXT TO SOMEBODY (Dan, 2026-08-27): 1 Cash for
 * EVERY occupied or claimed hex on the ring, on any tile you place. Two of
 * one rival's hexes is 2 Cash to that rival; two rivals with one each is
 * 1 Cash to each of them.
 *
 * IT IS THE ONE CHARGE THAT CAN LEAVE THE BOX. Every other price on the
 * board - the base cost, the copper wire, the TERRITORY_FEE - goes nowhere,
 * so crowding a rival cost the crowder something and the crowded nothing.
 * This is the half that was missing: their ground is worth money BECAUSE it
 * is theirs, so a tight map pays its landlords and there is a reason to sit
 * on a seam early.
 *
 * A HOME BASE IS GROUND LIKE ANY OTHER. The cube on an empty city is what
 * anchors its owner's whole network and is the one hex they can never lose,
 * so the ring around it is priced the same as the ring around a farm. This
 * is the one place the fee reads OWNERSHIP where the rest of the game's
 * counters read a tile.
 *
 * AND THE WORLD'S OWN FLEET CHARGES TOO, TO THE BANK. A 2026 starting plant
 * has no owner to hand 1 Cash to, so that one goes nowhere, like the
 * territory fee. Crowding is crowding whether the thing you are crowding
 * belongs to a rival or to the world - and the fleet's own ring is the
 * cheapest ground on the board to WIRE, which is exactly where the fee has
 * most work to do. An unowned EMPTY hex is still just empty ground.
 *
 * NOT WAIVED BY ANYTHING. The Transmission Superhighway and the Rural
 * Electrification Legacy bend the TERRITORY_FEE because that fee prices YOUR
 * reach, and wire is what they are about. This one is not a distance - it is
 * the price of the ground next door - so no card discounts it.
 *
 * Shared with the client for the standard-project text.
 */
export const NEIGHBOUR_FEE = 1;
/**
 * Learning curves: each Solar tag takes 1 Cash off Build Solar Farm and
 * each Wind tag 1 off Build Wind Farm, up to this much. The cap is what
 * stops a deep tableau from making the buildout free - the siting cost is
 * never discounted, so a farm always costs something to connect. Shared
 * with the client for the standard-project text.
 */
export const LEARNING_CURVE_MAX_DISCOUNT = 6;
/**
 * The ~15 PWh world of 2000 (the year-2000 fleet, 15 PWh as seated).
 *
 * IT WAS 16 UNTIL 2026-08-28, when the oil station was removed from the game
 * and the fleet's Osaka Bay tile went with it. The 1,200 TWh of Japanese
 * oil-fired generation it stood for is now simply not modelled - and 15 is
 * the closer figure anyway, the real world of 2000 having generated ~15,400
 * TWh against the 16 the board used to seat.
 *
 * THE GAP TO THE 2026 BOARD IS THE DECK'S TO CLOSE. This opens at 15 and
 * STARTING_ELECTRICITY_DEMAND_PWH at 31, so the 2000 scenario has 16 PWh to
 * travel by the generation that reaches 2025 - and with the baseline
 * escalator gone (Dan 2026-08-22) nothing but played cards moves demand at
 * all. The two scenarios therefore only agree at the year they share if the
 * table actually electrifies; a passive table leaves the 2000 board sitting
 * near 15. That is the intended trade: demand is the players' doing, not
 * the calendar's.
 */
export const STARTING_ELECTRICITY_DEMAND_PWH_2000 = 15;
export const STARTING_ELECTRICITY_SUPPLY_PWH_2000 = 15;
// Player tracks. MAX_EXCESS_CAPACITY went with the capacity tracks in
// 2026-08-16: capacity is the world's now, and the world's grid is not capped
// at 6 PWh. MAX_SOCIAL_ACCEPTANCE went the same way on 2026-08-19 (Dan's
// call): goodwill banked is goodwill kept, so acceptance is uncapped and the
// engines that pay out per point are free to run.
/** Placeholder storage cap; becomes commodity-specific with the Phase 5 fuel market. */
export const MAX_FUEL_STOCKPILE = 10;
export const OXYGEN_LEVEL_FOR_TEMPERATURE_BONUS = 8;
export const TEMPERATURE_FOR_OCEAN_BONUS = 0;
export const VENUS_LEVEL_FOR_CARD_BONUS = 8;
export const VENUS_LEVEL_FOR_TR_BONUS = 16;
export const ALT_VENUS_MINIMUM_BONUS = 16;
export const TEMPERATURE_BONUS_FOR_HEAT_1 = -24;
export const TEMPERATURE_BONUS_FOR_HEAT_2 = -20;
// Colonies
export const MAX_COLONY_TRACK_POSITION = 6;
export const MAX_COLONIES_PER_TILE = 3;
export const MAX_FLEET_SIZE = 4;
export const MC_TRADE_COST = 9;
export const ENERGY_TRADE_COST = 3;
export const TITANIUM_TRADE_COST = 3;
// Turmoil
export const DELEGATES_PER_PLAYER = 7;
export const DELEGATES_FOR_NEUTRAL_PLAYER = 14;
export const REDS_RULING_POLICY_COST = 3;
export const POLITICAL_AGENDAS_MAX_ACTION_USES = 3;
// Prelude
export const PRELUDE_CARDS_DEALT_PER_PLAYER = 4;
// Terrakeeping setup (Dan, 2026-09-06): the opening used to deal 4
// endowments (keep 2) and 3 secret objectives (keep 1), on top of 2
// coalitions, 2 cities and 10 project cards - eight decisions before the
// first turn. Each pick is one of two now, so the setup screen asks the
// same question of every deck it deals from.
export const ENDOWMENTS_DEALT_PER_PLAYER = 2;
export const ENDOWMENTS_KEPT_PER_PLAYER = 1;
export const SECRET_OBJECTIVES_DEALT_PER_PLAYER = 2;
// CEO
export const CEO_CARDS_DEALT_PER_PLAYER = 3;
// Promo
export const GRAPHENE_VALUE = 4;
// Map specific
export const HELLAS_BONUS_OCEAN_COST = 6;
export const VASTITAS_BOREALIS_BONUS_TEMPERATURE_COST = 3;
export const VASTITAS_BOREALIS_NOVA_BONUS_TEMPERATURE_COST = 4;
export const TERRA_CIMMERIA_COLONY_COST = 5;
// Moon
export const MAXIMUM_MOON_RATE = 8;
export const MAXIMUM_HABITAT_RATE = 8;
export const MAXIMUM_MINING_RATE = 8;
export const MAXIMUM_LOGISTIC_RATE = 8;
// Pathfinders
export const SEED_VALUE = 5;
export const DATA_VALUE = 3;
// Escape Velocity
export const DEFAULT_ESCAPE_VELOCITY_THRESHOLD = 30;
export const DEFAULT_ESCAPE_VELOCITY_BONUS_SECONDS = 2;
export const DEFAULT_ESCAPE_VELOCITY_PERIOD = 2;
export const DEFAULT_ESCAPE_VELOCITY_PENALTY = 1;
export const BONUS_SECONDS_PER_ACTION = 5;
// Leaders/CEOs
export const ASIMOV_AWARD_BONUS = 2;
export const ALL_LANGUAGES = ['en', 'de', 'fr', 'ru', 'cn', 'pl', 'es', 'br', 'it', 'ko', 'nl', 'hu', 'jp', 'bg', 'nb', 'ua', 'fi'];
export const LANGUAGES = {
    en: ['English', 'English'],
    de: ['Deutsch', 'German'],
    fr: ['Français', 'French'],
    ru: ['Русский', 'Russian'],
    cn: ['华语', 'Chinese'],
    pl: ['Polski', 'Polish'],
    es: ['Español', 'Spanish'],
    br: ['Português Brasileiro', 'Brazilian Portugese'],
    it: ['Italiano', 'Italian'],
    ko: ['한국어', 'Korean'],
    nl: ['Nederlands', 'Dutch'],
    hu: ['Magyar', 'Hungarian'],
    jp: ['日本語', 'Japanese'],
    bg: ['Български', 'Bulgarian'],
    nb: ['Norsk', 'Norwegian'],
    ua: ['Українська', 'Ukrainian'],
    fi: ['Suomi', 'Finnish'],
};
export const APP_NAME = 'Terrakeeping Earth';
export const DISCORD_INVITE = 'https://discord.gg/afeyggbN6Y';
export const DEFAULT_WAITING_FOR_TIMEOUT = 1000;
export const DEFAULT_LOG_LENGTH = 50;
export const DEFAULT_URL_ROOT = 'http://localhost:8080';
